/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPanel,
  EuiSkeletonText,
  EuiSpacer,
  EuiSuperSelectOption,
  EuiTab,
  EuiTabs,
  EuiText,
} from "@inscopix/ideas-eui";
import { ModalError } from "components/ModalError/ModalError";
import { ModalLoading } from "components/ModalLoading/ModalLoading";
import { UserWithAccessProps } from "components/ProjectSharing/UserWithAccess";
import { ApplicationUser, Project } from "graphql/_Types";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { useState, Fragment, useCallback } from "react";
import {
  USER_ACCESS_LEVELS_BY_KEY,
  UserAccessLevel,
} from "types/UserAccessLevels";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import {
  UserWithAccessExternal,
  UserWithAccessExternalProps,
} from "./UserWithAccessExternal";
import { addUtilityToastPointing } from "utils/addUtilityToastPointing";
import { ideasFeatures } from "ideas.features";
import { useIdeasSearchParams } from "hooks/useIdeasSearchParams";
import {
  AdminProjectDjango,
  getProjectDjango,
} from "django/libraryProjectDjango";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "django/queryKeys";
import { permissions, UserProjectAccessDjango } from "./permissions";
import { ProjectSharingDefaultAccessLevel } from "./ProjectSharingDefaultAccessLevel";
import { ProjectSharingInternalUserAccess } from "./ProjectSharingInternalUserAccess";
import { ButtonPermissionedBase } from "components/ButtonPermissioned/ButtonPermissioned";
import { FieldTextPermissionedBase } from "components/FieldTextPermissioned/FieldTextPermissioned";
import { CallOutError } from "components/CallOutError/CallOutError";
import { HelpIconTooltip } from "components/HelpIconTooltip/HelpIconTooltip";
import { useTourStep } from "providers/TourProvider/TourProvider.hooks";
import { TenantType } from "types/constants";
import useFindUser from "hooks/useFindUser";

type ProjectSharingBaseProps = {
  projectId: Project["id"];
  /**
   * Optional override for project query when accessing sharing modal for restricted projects
   * from org admin table
   */
  getProject?: (projectId: Project["id"]) => Promise<AdminProjectDjango>;
  /**
   * Optional override for default access level update when accessing sharing modal for restricted projects
   * from org admin table
   */
  updateInternalDefaultAccessLevel?: typeof permissions.userFunctions.internalAccess.updateInternalDefaultAccessLevel;
  createInternalUserAccess?: typeof permissions.userFunctions.internalAccess.createInternalUserAccess;
  revokeInternalUserAccess?: typeof permissions.userFunctions.internalAccess.revokeInternalUserAccess;
  updateInternalUserAccessLevel?: UserWithAccessProps["updateInternalUserAccessLevel"];
  revokeExternalProjectAccessInvitation?: UserWithAccessExternalProps["revokeExternalProjectAccessInvitation"];
  sendExternalProjectAccessInvitation?: UserWithAccessExternalProps["sendExternalProjectAccessInvitation"];
  resendExternalProjectAccessInvitation?: UserWithAccessExternalProps["resendExternalProjectAccessInvitation"];
  updateExternalProjectSharingInvitation?: UserWithAccessExternalProps["updateExternalProjectSharingInvitation"];
  getInternalUsersWithProjectAccess?: typeof permissions.userFunctions.internalAccess.getInternalUsersWithProjectAccess;
  getProjectSharingExternalInvitations?: typeof permissions.userFunctions.externalAccess.getProjectSharingExternalInvitations;
};

export interface ModalProjectSharingProps extends ProjectSharingBaseProps {
  mode: "modal";
  onClose: () => void;
}

export interface FlexProjectSharingProps extends ProjectSharingBaseProps {
  mode: "flex";
  onClose?: undefined;
}

export type ProjectSharingProps =
  | ModalProjectSharingProps
  | FlexProjectSharingProps;

export type TUserWithAccessExternal = {
  id: UserProjectAccessDjango["id"];
  email: string;
  invitee: ApplicationUser["id"] | null;
  status: number;
  project_access_level: UserAccessLevel["id"];
};

type TProjectSharingTab = "organization" | "external";

export const getPermissionData = async (
  projectId: Project["id"],
  getProject: NonNullable<ProjectSharingProps["getProject"]>,
  getInternalUsersWithProjectAccess: NonNullable<
    ProjectSharingProps["getInternalUsersWithProjectAccess"]
  >,
  getProjectSharingExternalInvitations: NonNullable<
    ProjectSharingProps["getProjectSharingExternalInvitations"]
  >,
) => {
  const usersWithProjectAccess = await getInternalUsersWithProjectAccess({
    projectId,
  });
  const project = await getProject(projectId);
  const externalUsersWithAccess = await getProjectSharingExternalInvitations({
    projectId,
  });

  return { project, usersWithProjectAccess, externalUsersWithAccess };
};

export const useInvalidateModalProjectSharingQuery = () => {
  const queryClient = useQueryClient();

  const invalidateModalProjectSharingQuery = useCallback(
    async (projectId: Project["id"]) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.modalProjectShare(projectId),
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeys.permissionsProvider(projectId),
        exact: true,
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeys.getAdminProjects(),
      });
    },
    [queryClient],
  );
  return { invalidateModalProjectSharingQuery };
};

const accessLevelOptions: EuiSuperSelectOption<`${UserAccessLevel["id"]}`>[] = [
  // All internal users with "Viewer" permission are invited as copiers.
  // Only external users with have the true "Viewer" role.
  {
    value: `${USER_ACCESS_LEVELS_BY_KEY["COPIER"].id}`,
    inputDisplay: <EuiText size="s">Viewer</EuiText>,
    dropdownDisplay: (
      <>
        <strong>Viewer</strong>
        <EuiText size="s" color="subdued">
          <p>View project and files only</p>
        </EuiText>
      </>
    ),
  },
  {
    value: `${USER_ACCESS_LEVELS_BY_KEY["EDITOR"].id}`,
    inputDisplay: <EuiText size="s">Editor</EuiText>,
    dropdownDisplay: (
      <>
        <strong>Editor</strong>
        <EuiText size="s" color="subdued">
          <p>Create/edit tables, upload files, and run analyses</p>
        </EuiText>
      </>
    ),
  },
  {
    value: `${USER_ACCESS_LEVELS_BY_KEY["ADMIN"].id}`,
    inputDisplay: <EuiText size="s">Admin</EuiText>,
    dropdownDisplay: (
      <>
        <strong>Admin</strong>
        <EuiText size="s" color="subdued">
          <p>Manage access, delete project</p>
        </EuiText>
      </>
    ),
  },
];

export const ProjectSharing = ({
  projectId,
  mode,
  getProject = getProjectDjango,
  updateInternalDefaultAccessLevel = permissions.userFunctions.internalAccess
    .updateInternalDefaultAccessLevel,
  createInternalUserAccess = permissions.userFunctions.internalAccess
    .createInternalUserAccess,
  revokeInternalUserAccess = permissions.userFunctions.internalAccess
    .revokeInternalUserAccess,
  updateInternalUserAccessLevel = permissions.userFunctions.internalAccess
    .updateInternalUserAccessLevel,
  sendExternalProjectAccessInvitation = permissions.userFunctions.externalAccess
    .sendExternalProjectAccessInvitation,
  resendExternalProjectAccessInvitation = permissions.userFunctions
    .externalAccess.resendExternalProjectAccessInvitation,
  revokeExternalProjectAccessInvitation = permissions.userFunctions
    .externalAccess.revokeExternalProjectAccessInvitation,
  updateExternalProjectSharingInvitation = permissions.userFunctions
    .externalAccess.updateExternalProjectSharingInvitation,
  getInternalUsersWithProjectAccess = permissions.userFunctions.internalAccess
    .getInternalUsersWithProjectAccess,
  getProjectSharingExternalInvitations = permissions.userFunctions
    .externalAccess.getProjectSharingExternalInvitations,
  onClose: onCloseFromProps,
}: ProjectSharingProps) => {
  const { invalidateModalProjectSharingQuery } =
    useInvalidateModalProjectSharingQuery();

  const { data, isLoading, isRefetchError, isLoadingError } = useQuery({
    queryFn: () =>
      getPermissionData(
        projectId,
        getProject,
        getInternalUsersWithProjectAccess,
        getProjectSharingExternalInvitations,
      ),
    queryKey: queryKeys.modalProjectShare(projectId),
    refetchOnMount: "always",
  });

  const isPersonalSpaceProject =
    data?.project.tenant.tenant_type === TenantType.PERSONAL;

  const { getParam, deleteParam } = useIdeasSearchParams();
  const onClose = useCallback(() => {
    if (mode === "modal") {
      const isShareRouteParamSet = getParam("OPEN_MODAL") === "shareProject";
      if (isShareRouteParamSet) {
        deleteParam("OPEN_MODAL");
      }
      onCloseFromProps();
    }
  }, [deleteParam, getParam, mode, onCloseFromProps]);

  const currentUser = useUserContext((s) => s.currentUser);
  const tenantUsers = useTenantContext((s) => s.tenantUsers);

  const [selectedTab, setSelectedTab] =
    useState<TProjectSharingTab>("organization");

  const [emailToInvite, setEmailToInvite] = useState<string>("");
  const [sendingInvite, setSendingInvite] = useState<boolean>(false);

  const { findUser } = useFindUser();

  const { component: TourProjectSharing1, anchorId: anchorId1 } = useTourStep(
    "TourProjectSharing",
    1,
  );
  const { component: TourProjectSharing3, anchorId: anchorId3 } = useTourStep(
    "TourProjectSharing",
    3,
  );
  const { component: TourProjectSharingPS1 } = useTourStep(
    "TourProjectSharingPS",
    1,
  );

  const handleInviteUser = async () => {
    if (emailToInvite === currentUser.email) {
      ideasFeatures.devOnly
        ? addUtilityToastPointing("You cannot invite yourself")
        : addUtilityToastFailure("You cannot invite yourself");
      return;
    }
    if (tenantUsers.some((user) => user.email === emailToInvite)) {
      addUtilityToastFailure(
        "User already exists in the organization - use the organization access tab to share this project with them",
      );
      return;
    }
    setSendingInvite(true);

    // until we have personal space projects/self-sign up, we need to check if the user exists
    if (!ideasFeatures.personalSpace) {
      try {
        // Check if the user exists
        await findUser(emailToInvite);
      } catch (error) {
        addUtilityToastFailure(
          `No current IDEAS user found with this email address: ${emailToInvite}. Inviting new users to IDEAS will be possible in the next release.`,
        );
        setSendingInvite(false);
        return;
      }
    }

    try {
      await sendExternalProjectAccessInvitation({
        projectId,
        email: emailToInvite,
      });

      setEmailToInvite("");

      addUtilityToastSuccess("User invited");
    } catch (error) {
      addUtilityToastFailure("Failed to invite user");
    }

    await invalidateModalProjectSharingQuery(projectId);
    setSendingInvite(false);
  };

  if (isLoading) {
    return mode === "modal" ? (
      <ModalLoading onClose={onClose} />
    ) : (
      <EuiSkeletonText lines={5} />
    );
  }

  if (data === undefined || isRefetchError || isLoadingError) {
    return mode === "modal" ? (
      <ModalError onClose={onClose} />
    ) : (
      <CallOutError />
    );
  }

  const userHasGrantAccessPermission = data.project.permissions.grant_access;

  // don't show organization access for personal space projects
  const organizationTab = isPersonalSpaceProject ? null : (
    <Fragment>
      <EuiPanel
        hasShadow={false}
        hasBorder={true}
        // stops the panels from trying to match heights in flex mode
        css={css`
          flex-grow: 0;
        `}
      >
        {mode === "flex" && (
          <>
            <EuiText size="s">
              <h3 id={anchorId1}>
                Organization Access{" "}
                <HelpIconTooltip
                  content={
                    <>
                      <p>
                        Set the access level to this project for all users in
                        the organization this project belongs to or assign
                        individual access levels to organization members.
                      </p>
                    </>
                  }
                  size="m"
                />
                {TourProjectSharing1}
              </h3>
            </EuiText>
            <EuiSpacer size="s" />
          </>
        )}
        <EuiForm>
          <ProjectSharingDefaultAccessLevel
            userHasGrantAccessPermission={userHasGrantAccessPermission}
            accessLevelOptions={accessLevelOptions}
            updateInternalDefaultAccessLevel={updateInternalDefaultAccessLevel}
            projectId={projectId}
            defaultAccessLevel={data.project.default_user_access_level}
          />

          <EuiSpacer size="l" />

          <ProjectSharingInternalUserAccess
            userHasGrantAccessPermission={userHasGrantAccessPermission}
            accessLevelOptions={accessLevelOptions}
            project={data.project}
            revokeInternalUserAccess={revokeInternalUserAccess}
            updateInternalUserAccessLevel={updateInternalUserAccessLevel}
            createInternalUserAccess={createInternalUserAccess}
            usersWithAccess={data.usersWithProjectAccess}
          />
        </EuiForm>
      </EuiPanel>
    </Fragment>
  );

  const externalSharingTooltipContent = isPersonalSpaceProject ? (
    <>
      <p>
        Projects in a personal space be be shared by inviting users by email
        address. These users will only have the capability to view and not edit
        projects.
      </p>
      <EuiSpacer size="s" />
      <p>
        You may choose whether or not to allow invited viewers to download
        files.
      </p>
    </>
  ) : (
    <>
      <p>
        Share with people who are not members of the organization this project
        belongs to.
      </p>
      <EuiSpacer size="s" />
      <p>
        Note that without organization access these users will only have the
        capability to view and not edit projects.
      </p>
      <EuiSpacer size="s" />
      <p>
        You may choose whether or not to allow invited viewers to download
        files.
      </p>
    </>
  );

  const externalTab = (
    <Fragment>
      <EuiPanel
        hasShadow={false}
        hasBorder={true}
        // flex-grow: 0 stops the panels from trying to match heights in flex mode
        // max width prevents the panel from stretching too wide when only one panel is shown (personal space)
        css={css`
          flex-grow: 0;
          max-width: 580px;
        `}
      >
        {mode === "flex" && (
          <>
            <EuiText size="s">
              <h3 id={anchorId3}>
                External Sharing{" "}
                <HelpIconTooltip
                  content={externalSharingTooltipContent}
                  size="m"
                />
                {TourProjectSharing3}
                {TourProjectSharingPS1}
              </h3>
            </EuiText>
            <EuiSpacer size="s" />
          </>
        )}
        <EuiText size="xs">
          <h4>External users with access</h4>
        </EuiText>
        <EuiSpacer size="s" />

        <EuiPanel hasShadow={false} hasBorder={true}>
          {data.externalUsersWithAccess.length === 0 && (
            <EuiCallOut>
              No external users have been invited to access this project
            </EuiCallOut>
          )}
          {data.externalUsersWithAccess.map((usersWithAccess, idx) => {
            return (
              <Fragment key={usersWithAccess.id}>
                <UserWithAccessExternal
                  userHasGrantAccessPermission={userHasGrantAccessPermission}
                  projectId={projectId}
                  key={usersWithAccess.id}
                  invite={usersWithAccess}
                  sendExternalProjectAccessInvitation={
                    sendExternalProjectAccessInvitation
                  }
                  resendExternalProjectAccessInvitation={
                    resendExternalProjectAccessInvitation
                  }
                  revokeExternalProjectAccessInvitation={
                    revokeExternalProjectAccessInvitation
                  }
                  updateExternalProjectSharingInvitation={
                    updateExternalProjectSharingInvitation
                  }
                />
                {/* Put space between items except for the last child */}
                {idx + 1 < data.externalUsersWithAccess.length && (
                  <EuiSpacer size="m" />
                )}
              </Fragment>
            );
          })}
        </EuiPanel>

        <EuiSpacer size="l" />
        <EuiFormRow label="Invite external users" fullWidth>
          <EuiFlexGroup gutterSize="s">
            <EuiFlexItem>
              <FieldTextPermissionedBase
                type="email"
                placeholder="Enter email address"
                value={emailToInvite}
                onChange={(e) => {
                  setEmailToInvite(e.target.value);
                }}
                isPermitted={userHasGrantAccessPermission}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <ButtonPermissionedBase
                isPermitted={userHasGrantAccessPermission}
                isLoading={sendingInvite}
                size="m"
                onClick={() => void handleInviteUser()}
              >
                Invite user
              </ButtonPermissionedBase>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiCallOut
          size="s"
          color="primary"
          title="External users can only have 'View' access"
          iconType="iInCircle"
        />
      </EuiPanel>
    </Fragment>
  );

  if (mode === "flex") {
    return (
      <EuiFlexGroup>
        {
          // don't show organization access for personal space projects
          !isPersonalSpaceProject && (
            <EuiFlexItem>{organizationTab}</EuiFlexItem>
          )
        }
        <EuiFlexItem>{externalTab}</EuiFlexItem>
      </EuiFlexGroup>
    );
  }

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        min-width: 540px;
        // matches the height to the max-block-size of EuiModals so it stays constant
        height: 75vh;
      `}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle>Share Project</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        {
          // don't show tabs for personal space projects
          !isPersonalSpaceProject && (
            <EuiTabs>
              <EuiTab
                onClick={() => setSelectedTab("organization")}
                isSelected={selectedTab === "organization"}
              >
                Organization Access
              </EuiTab>
              <EuiTab
                onClick={() => setSelectedTab("external")}
                isSelected={selectedTab === "external"}
              >
                External access
              </EuiTab>
            </EuiTabs>
          )
        }
        {
          // don't show org tab for personal space projects
          selectedTab === "organization" && !isPersonalSpaceProject
            ? organizationTab
            : externalTab
        }
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Close</EuiButtonEmpty>
      </EuiModalFooter>
    </EuiModal>
  );
};
